"use client";

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import Drawer, { type DrawerSide, type DrawerSize } from "./Drawer";

export interface DrawerOpenOptions {
    title?: ReactNode;
    content: ReactNode;
    header?: ReactNode;
    footer?: ReactNode;
    size?: DrawerSize;
    side?: DrawerSide;
    closeOnOverlayClick?: boolean;
    closeOnEsc?: boolean;
    showCloseButton?: boolean;
    panelClassName?: string;
    overlayClassName?: string;
    headerClassName?: string;
    bodyClassName?: string;
    footerClassName?: string;
    onOpenChange?: (open: boolean) => void;
}

interface DrawerControllerContextValue {
    open: (options: DrawerOpenOptions) => void;
    close: () => void;
    isOpen: boolean;
}

const DrawerControllerContext = createContext<DrawerControllerContextValue | null>(null);

export const useDrawer = () => {
    const ctx = useContext(DrawerControllerContext);
    if (!ctx) {
        throw new Error("useDrawer must be used within <DrawerProvider>");
    }
    return ctx;
};

export const DrawerProvider = ({ children }: { children: ReactNode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<DrawerOpenOptions | null>(null);
    const onOpenChangeRef = useRef<((open: boolean) => void) | null>(null);

    const open = useCallback((nextOptions: DrawerOpenOptions) => {
        onOpenChangeRef.current = nextOptions.onOpenChange ?? null;
        setOptions(nextOptions);
        setIsOpen(true);
        nextOptions.onOpenChange?.(true);
    }, []);

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setIsOpen(nextOpen);
        onOpenChangeRef.current?.(nextOpen);
    }, []);

    const close = useCallback(() => {
        handleOpenChange(false);
    }, [handleOpenChange]);

    const controller = useMemo<DrawerControllerContextValue>(
        () => ({ open, close, isOpen }),
        [open, close, isOpen]
    );

    return (
        <DrawerControllerContext.Provider value={controller}>
            {children}
            <Drawer
                open={isOpen}
                onOpenChange={handleOpenChange}
                title={options?.title}
                header={options?.header}
                footer={options?.footer}
                size={options?.size}
                side={options?.side}
                closeOnOverlayClick={options?.closeOnOverlayClick}
                closeOnEsc={options?.closeOnEsc}
                showCloseButton={options?.showCloseButton}
                panelClassName={options?.panelClassName}
                overlayClassName={options?.overlayClassName}
                headerClassName={options?.headerClassName}
                bodyClassName={options?.bodyClassName}
                footerClassName={options?.footerClassName}
            >
                {options?.content}
            </Drawer>
        </DrawerControllerContext.Provider>
    );
};
